import {Switch, Tooltip} from 'antd';
import {useEffect} from 'react';

import {AdditionalInfo} from '../../../../../layout/additional-info/additional-info';
import {Locale} from '../../../../../provider/locale/locale';
import {
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
} from '../../../../../service/yandex-stories/yandex-stories-type';
import {Form, FormInstance} from '../../../../../typings/antd';
import {classNames} from '../../../../../util/css';
import {Text} from '../../../../text/text';

import * as styles from './yandex-story-auto-add-companies-switch.scss';

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
    // The backend expands an auto-add story to every company of its brand, so the option only makes
    // sense once the whole brand is selected.
    isAvailable: boolean;
    // Per the design the control is hidden entirely while nothing is selected.
    isVisible: boolean;
    disabled?: boolean;
};

export function YandexStoryAutoAddCompaniesSwitch(props: PropsType): JSX.Element | null {
    const {formInstance, isAvailable, isVisible, disabled} = props;

    const isAutoAddEnabled =
        Form.useWatch(YandexStoryFormFieldsEnum.AutoAddCompanies, {form: formInstance, preserve: true}) ?? false;

    // Narrowing the selection after enabling the option would send the backend a story it expands
    // back to the whole brand, so the flag is dropped as soon as it stops being applicable.
    useEffect(() => {
        if (!isAvailable && isAutoAddEnabled) {
            formInstance.setFieldValue(YandexStoryFormFieldsEnum.AutoAddCompanies, false);
        }
    }, [formInstance, isAutoAddEnabled, isAvailable]);

    const isSwitchDisabled = Boolean(disabled) || !isAvailable;

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles.YandexStoryAutoAddCompaniesSwitch}>
            <AdditionalInfo rightNode={<Text stringKey="YANDEX_STORY__AUTO_ADD_COMPANIES__LABEL" />}>
                <Text stringKey="YANDEX_STORY__AUTO_ADD_COMPANIES__TOOLTIP" />
            </AdditionalInfo>

            <Tooltip
                title={isSwitchDisabled ? <Locale stringKey="YANDEX_STORY__AUTO_ADD_COMPANIES__UNAVAILABLE" /> : ''}
            >
                <span
                    className={classNames(styles.YandexStoryAutoAddCompaniesSwitch_switch, {
                        [styles.YandexStoryAutoAddCompaniesSwitch_switch__disabled]: isSwitchDisabled,
                    })}
                >
                    <Switch
                        checked={isAutoAddEnabled}
                        disabled={isSwitchDisabled}
                        onChange={(isChecked) =>
                            formInstance.setFieldValue(YandexStoryFormFieldsEnum.AutoAddCompanies, isChecked)
                        }
                    />
                </span>
            </Tooltip>
        </div>
    );
}
